'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

type UseStickyWhenHiddenOptions = {
  topOffset?: number
}

export const useStickyWhenHidden = (
  options: UseStickyWhenHiddenOptions = {},
) => {
  const { topOffset = 0 } = options

  const [isSticky, setIsSticky] = useState(false)

  const elementRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const rafIdRef = useRef<number | null>(null)
  const isStickyRef = useRef(false)

  isStickyRef.current = isSticky

  const handleScroll = useCallback(() => {
    const stuck = isStickyRef.current

    if (!stuck && elementRef.current) {
      const rect = elementRef.current.getBoundingClientRect()

      if (rect.bottom <= 0) {
        setIsSticky(true)
      }
    } else if (stuck && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect()

      if (rect.top >= topOffset) {
        setIsSticky(false)
      }
    }
  }, [topOffset])

  const scrollHandler = useCallback(() => {
    if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current)
    rafIdRef.current = requestAnimationFrame(() => {
      handleScroll()
      rafIdRef.current = null
    })
  }, [handleScroll])

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler, { passive: true })

    return () => {
      window.removeEventListener('scroll', scrollHandler)
    }
  }, [scrollHandler])

  return {
    elementRef,
    containerRef,
    isSticky,
  }
}
